import { merchantApi } from '@/config/axios'

/**
 * 商家管理中心 - Dashboard API
 * 全部使用 merchantApi（baseURL 不含 /admin-api）
 * ignoreError：概况页聚合多权限接口，缺权时静默降级，避免 Access Denied 刷屏
 */

const softHeaders = { ignoreError: true }

/** 概况页钱包：缺权静默 */
export function getWalletSoft() {
  return merchantApi
    .get({
      url: '/merchant-api/finance/wallet',
      headers: softHeaders
    })
    .catch(() => ({}))
}

// 获取当前商家店铺信息
export function getShopInfo() {
  return merchantApi
    .get({
      url: '/merchant-api/merchant/shop/get',
      headers: softHeaders
    })
    .catch(() => ({}))
}

// 获取今日订单数量
export function getTodayOrderCount() {
  return merchantApi
    .get({
      url: '/merchant-api/order/page',
      params: { pageNo: 1, pageSize: 1, createTimeStart: getTodayStart(), createTimeEnd: getTodayEnd() },
      headers: softHeaders
    })
    .catch(() => ({ total: 0 }))
}

// 获取累计订单数量
export function getOrderCount() {
  return merchantApi
    .get({
      url: '/merchant-api/order/page',
      params: { pageNo: 1, pageSize: 1 },
      headers: softHeaders
    })
    .catch(() => ({ total: 0 }))
}

// 获取待发货订单数量
export function getPendingShipCount() {
  return merchantApi
    .get({
      url: '/merchant-api/order/page',
      params: { pageNo: 1, pageSize: 1, status: 10 },
      headers: softHeaders
    })
    .catch(() => ({ total: 0 }))
}

// 获取商品总数
export function getProductCount() {
  return merchantApi
    .get({
      url: '/merchant-api/product/spu/page',
      params: { pageNo: 1, pageSize: 1 },
      headers: softHeaders
    })
    .catch(() => ({ total: 0 }))
}

// 获取在售商品数
export function getOnSaleProductCount() {
  return merchantApi
    .get({
      url: '/merchant-api/product/spu/page',
      params: { pageNo: 1, pageSize: 1, status: 1 },
      headers: softHeaders
    })
    .catch(() => ({ total: 0 }))
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function formatYmd(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function getTodayStart() {
  return `${formatYmd(new Date())} 00:00:00`
}

function getTodayEnd() {
  return `${formatYmd(new Date())} 23:59:59`
}

function dayRange(offsetDays: number) {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + offsetDays)
  const ymd = formatYmd(d)
  return {
    label: `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    start: `${ymd} 00:00:00`,
    end: `${ymd} 23:59:59`
  }
}

/** 近 7 日订单量（按创建时间，复用订单分页 total） */
export async function getOrderTrendLast7Days() {
  const days = Array.from({ length: 7 }, (_, i) => dayRange(i - 6))
  const results = await Promise.all(
    days.map((day) =>
      merchantApi
        .get({
          url: '/merchant-api/order/page',
          params: {
            pageNo: 1,
            pageSize: 1,
            createTimeStart: day.start,
            createTimeEnd: day.end
          },
          headers: softHeaders
        })
        .catch(() => ({ total: 0 }))
    )
  )
  return {
    dates: days.map((d) => d.label),
    orderCount: results.map((r: any) => r?.total || 0)
  }
}

const ORDER_STATUS_LABELS: { status: number; name: string }[] = [
  { status: 0, name: '待付款' },
  { status: 10, name: '待发货' },
  { status: 20, name: '已发货' },
  { status: 30, name: '已完成' },
  { status: 40, name: '已取消' }
]

/** 订单状态分布（各状态 total） */
export async function getOrderStatusDistribution() {
  const results = await Promise.all(
    ORDER_STATUS_LABELS.map((item) =>
      merchantApi
        .get({
          url: '/merchant-api/order/page',
          params: { pageNo: 1, pageSize: 1, status: item.status },
          headers: softHeaders
        })
        .catch(() => ({ total: 0 }))
        .then((res: any) => ({
          name: item.name,
          value: res?.total || 0
        }))
    )
  )
  return results
}
